import { useEffect, useRef, useState } from 'react';

import { IconButton as ButtonIcon, Box as Flex, Link } from '@material-ui/core';

import { RiFileCopyLine, RiPencilLine } from '@remixicon/react';
import { getMarkRange } from '@tiptap/core';

import { useCurrentEditor } from '../Context';
import { FloatingPopover } from '../FloatingPopover';
import { linkClassName } from './Link';
import { LINK_DIALOG_OPEN_EVENT } from './constants';

const SHOW_DELAY_MS = 200;
const HIDE_DELAY_MS = 150;

export const LinkHoverToolbar = () => {
  const { editor } = useCurrentEditor();
  const floatingRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<{
    element: HTMLElement;
    href: string;
  } | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();
  const isOverToolbarRef = useRef(false);

  // Main editor effect - handle link hover and editor mouseleave
  useEffect(() => {
    if (!editor?.isEditable || editor.isDestroyed) return undefined;

    const handleEditorMouseMove = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const linkElement = target.closest(`a.${linkClassName}`);

      if (linkElement instanceof HTMLElement) {
        const href = linkElement.getAttribute('href');
        if (!href) return;

        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = undefined;
        }

        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
        }

        showTimeoutRef.current = setTimeout(() => {
          setHoveredLink({ element: linkElement, href });
          setIsVisible(true);
          showTimeoutRef.current = undefined;
        }, SHOW_DELAY_MS);
      }
    };

    const handleEditorMouseLeave = () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = undefined;
      }

      // Only hide if we're not over the toolbar
      if (!isOverToolbarRef.current) {
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setHoveredLink(null);
          hideTimeoutRef.current = undefined;
        }, HIDE_DELAY_MS);
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('mousemove', handleEditorMouseMove);
    editorElement.addEventListener('mouseleave', handleEditorMouseLeave);

    return () => {
      editorElement.removeEventListener('mousemove', handleEditorMouseMove);
      editorElement.removeEventListener('mouseleave', handleEditorMouseLeave);

      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [editor]);

  // Toolbar hover tracking effect
  useEffect(() => {
    const toolbarElement = floatingRef.current;
    if (!toolbarElement || !isVisible) return undefined;

    const handleMouseEnter = () => {
      isOverToolbarRef.current = true;
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = undefined;
      }
    };

    const handleMouseLeave = () => {
      isOverToolbarRef.current = false;
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setHoveredLink(null);
        hideTimeoutRef.current = undefined;
      }, HIDE_DELAY_MS);
    };

    toolbarElement.addEventListener('mouseenter', handleMouseEnter);
    toolbarElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      toolbarElement.removeEventListener('mouseenter', handleMouseEnter);
      toolbarElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible]);

  const handleCopy = async () => {
    if (!hoveredLink) return;
    await navigator.clipboard.writeText(hoveredLink.href);
  };

  const handleEdit = () => {
    if (!hoveredLink || !editor) return;

    const domPos = editor.view.posAtDOM(hoveredLink.element, 0);
    const $pos = editor.state.doc.resolve(domPos);
    const range = getMarkRange($pos, editor.state.schema.marks.link);

    if (range) {
      const { from, to } = range;
      editor.chain().focus().setTextSelection({ from, to }).run();
      window.dispatchEvent(new CustomEvent(LINK_DIALOG_OPEN_EVENT));
    }

    setIsVisible(false);
  };

  return (
    <FloatingPopover
      ref={floatingRef}
      isOpen={isVisible && hoveredLink !== null}
      anchorElement={hoveredLink?.element ?? null}
      closeOnClickOutside={false}
      py="1"
      px="2"
      maxWidth="300px"
      width="fit-content"
    >
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 'var(--encore-spacing-tighter-4)',
        }}
      >
        <Link
          href={hoveredLink?.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {hoveredLink?.href}
        </Link>
        <ButtonIcon size="small" aria-label="Copy link" onClick={handleCopy}>
          <RiFileCopyLine />
        </ButtonIcon>
        <ButtonIcon size="small" aria-label="Edit link" onClick={handleEdit}>
          <RiPencilLine />
        </ButtonIcon>
      </Flex>
    </FloatingPopover>
  );
};
